import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grid, Card, CardContent, Box, Typography } from '@mui/material'
import T from 'T'
import SchedulingFiltersPanel from 'components/configured-service/Schedule/SchedulingFiltersPanel'
import SchedulingMapLite from 'components/configured-service/Schedule/SchedulingMapLite'
import { FormProvider, useFormContext } from 'react-hook-form'
import CSPricingListViewSmallCell from 'components/configured-service/CreateDraftConfiguredServiceDialog/CSPricingListViewSmallCell'
import { isYearlyRecurrence } from 'utils/service'
import { findRecurrenceOptionByKey } from 'components/configured-service/EditServiceRecurrenceDialog/utils/recurrenceOptionsHelper'
import CreateServiceRecurrenceForm from 'components/configured-service/CreateDraftConfiguredServiceDialog/components/CreateServiceRecurrenceForm'
import DraftServiceEventsPreview from 'components/configured-service/CreateDraftConfiguredServiceDialog/ScheduleTabDraft/components/DraftServiceEventsPreview'
import { useScheduleFiltersForm } from 'components/configured-service/CreateDraftConfiguredServiceDialog/ScheduleTabDraft/hooks/useScheduleFiltersForm'
import { useScheduleMapAndLocation } from 'components/configured-service/CreateDraftConfiguredServiceDialog/ScheduleTabDraft/hooks/useScheduleMapAndLocation'
import { useScheduleRecurrenceDraft } from 'components/configured-service/CreateDraftConfiguredServiceDialog/ScheduleTabDraft/hooks/useScheduleRecurrenceDraft'

const ScheduleTabDraft = ({ metaFiltersData, onRegisterFiltersReset }) => {
  const { watch } = useFormContext()
  const selectedPricedService = watch('selectedPricedService')

  const { isScheduleTab, recurrenceOptions, recurrenceFormData, draftBaselineDefaults, serviceData, existingEvents, serviceStartDate } =
    useScheduleRecurrenceDraft({ metaFiltersData })

  const { filtersMethods, businessLineOptions, materialOptions, measureOptions, serviceActionOptions } = useScheduleFiltersForm({
    metaFiltersData,
    onRegisterFiltersReset,
    serviceStartDate,
  })

  const { latitude, longitude, mapCenter, handleMapCenterChange, isLoading: isMapLoading } = useScheduleMapAndLocation()

  const isYearlySelected = useMemo(() => {
    const selected = findRecurrenceOptionByKey(recurrenceOptions, recurrenceFormData?.serviceInterval)
    return isYearlyRecurrence(selected?.recurrenceInterval)
  }, [recurrenceOptions, recurrenceFormData?.serviceInterval])

  return (
    <Box sx={{ width: '100%', height: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
      <Grid container sx={{ height: '100%', overflow: 'hidden', width: '100%', minWidth: 1200 }}>
        <FormProvider {...filtersMethods}>
          <Grid size="auto" sx={{ height: '100%', overflowY: 'auto', width: 355, minWidth: 355 }}>
            <SchedulingFiltersPanel
              businessLineOptions={businessLineOptions}
              materialOptions={materialOptions}
              measureOptions={measureOptions}
              serviceActionOptions={serviceActionOptions}
              mapCenter={mapCenter}
            />
          </Grid>
          <Grid size="grow">
            {isScheduleTab && (
              <SchedulingMapLite
                latitude={latitude}
                longitude={longitude}
                onCenterChange={handleMapCenterChange}
                isAccountLoading={isMapLoading}
              />
            )}
          </Grid>
        </FormProvider>
        <Grid size="auto" container sx={{ height: '100%', overflowY: 'auto', width: 355, minWidth: 355 }}>
          <Card elevation={0}>
            <CardContent>
              <Grid size={12}>
                <Box sx={{ position: 'relative', mt: 1.25 }}>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1,
                      pb: 0.5,
                    }}
                  >
                    <CSPricingListViewSmallCell row={selectedPricedService} />
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: 'translateX(-50%) translateY(-50%)',
                      bgcolor: 'background.paper',
                      px: 0.5,
                      fontWeight: 600,
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {T.SELECTED_SERVICE}
                  </Typography>
                </Box>
              </Grid>
              {isScheduleTab && (
                <Grid size={12}>
                  <Box mt={2}>
                    <CreateServiceRecurrenceForm metaFiltersData={metaFiltersData} showYearly isCompact />
                  </Box>
                </Grid>
              )}
              {isScheduleTab && !isYearlySelected && (
                <Box mt={2} mb={2}>
                  <DraftServiceEventsPreview
                    open
                    startDate={serviceStartDate}
                    formData={recurrenceFormData}
                    defaultValues={draftBaselineDefaults}
                    recurrenceOptions={recurrenceOptions}
                    serviceData={serviceData}
                    existingEvents={existingEvents}
                    isNewService
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

ScheduleTabDraft.propTypes = {
  metaFiltersData: PropTypes.object,
  onRegisterFiltersReset: PropTypes.func,
}

export default ScheduleTabDraft
